import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    DataGrid,
    GridColDef,
    esES,
    GridValueGetterParams,
    GridRenderCellParams,
    GridRowParams,
    GridActionsCellItem,
} from '@mui/x-data-grid';
import moment from "moment";
import { cx } from 'class-variance-authority';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
export interface ICall {
    id: string;
    from: string;
    to: string;
    status: CallStatusType;
    duration: number;
    startedAt: Date;
}

type CallStatusType = 
  | 'queued'
  | 'ringing'
  | 'in-progress'
  | 'canceled'
  | 'completed'
  | 'failed'
  | 'busy'
  | 'no-answer';

const statusText: Record<CallStatusType, string> = {
    queued: 'En cola',
    ringing: 'Sonando',
    'in-progress': 'En progreso',
    canceled: 'Cancelada',
    completed: 'Completada',
    failed: 'Fallida',
    busy: 'Ocupado',
    'no-answer': 'Sin respuesta',
};


const CallsTable = (props: {
    data: ICall[];
    onViewRecords: (callId: string) => void; 
}) => {
    const { data: rows } = props;

    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
    };

    const columns: GridColDef[] = useMemo(() => [
        {
            field: 'startedAt',
            headerName: 'Inicio',
            width: 150,
            sortable: false,
            editable: false,
            valueGetter: (params: GridValueGetterParams<ICall>) => {
                return moment(params.row.startedAt).format('DD/MM/YY hh:mm a');
            },
        },
        {
            field: 'from',
            headerName: 'Origen',
            width: 130,
            sortable: false,
            editable: false,
            valueGetter: (params: GridValueGetterParams<ICall>) => {
                return params.row.from;
            },
        },
        {
            field: 'to',
            headerName: 'Destino',
            width: 130,
            sortable: false,
            editable: false,
            valueGetter: (params: GridValueGetterParams<ICall>) => {
                return params.row.to;
            },
        },
        {
            field: 'status',
            headerName: 'Estado',
            width: 120,
            sortable: false,
            editable: false,
            renderCell: (params: GridRenderCellParams<ICall>) => (
                <div className={cx({
                    'rounded-md p-2 text-white font-semibold': true,
                    'bg-yellow-300': params.row.status === 'queued',
                    'bg-orange-500': params.row.status === 'ringing',
                    'bg-blue-500': params.row.status === 'in-progress',
                    'bg-gray-500': params.row.status === 'canceled',
                    'bg-green-500': params.row.status === 'completed',
                    'bg-red-800': params.row.status === 'failed',
                    'bg-red-500': params.row.status === 'busy',
                    'bg-amber-700': params.row.status === 'no-answer',
                })}>
                    {statusText[params.row.status]}
                </div>
            ),
        },
        {
            field: 'duration',
            headerName: 'Duración',
            width: 100,
            sortable: false,
            editable: false,
            renderCell: (params: GridRenderCellParams<ICall>) => (
                <div>
                    {formatTime(params.row.duration)}
                </div>
            ),
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            editable: false,
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem
                    icon={<GraphicEqIcon />}
                    label="Grabaciones"
                    onClick={() => {
                        props.onViewRecords(params.row.id);
                    }} />,
            ]
        }
    ], []);

    return (
        <>
            <DataGrid
                initialState={{
                    pagination: {
                        paginationModel: { pageSize: 25, page: 0 },
                    },
                }}
                getRowId={(row: ICall) => row.id}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                autoHeight
                rowSelection={false}
                columns={columns}
                rows={rows}
            />
        </>
    );

};

export { CallsTable };